import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AuthConfig, Principal } from './types';
import { getCurrentPrincipal, getAuthConfig } from '../lib/api/platformApi';
import { configureApiAuthHandlers } from '../lib/api/client';
import {
  authenticateWithPassword,
  confirmPasswordReset as confirmPasswordResetWithCognito,
  createHostedLoginRequest,
  exchangeAuthorizationCode,
  hasOauthCallback,
  requestPasswordReset as requestPasswordResetWithCognito,
  refreshHostedSession,
  tokenExpired,
  type CognitoTokens,
} from '../features/auth/cognito';

interface StoredSession {
  readonly mode: 'password' | 'oauth';
  readonly principal: Principal;
  readonly tokens?: CognitoTokens;
}

interface SessionContextValue {
  readonly principal: Principal | null;
  readonly authConfig: AuthConfig | null;
  readonly authBootstrapped: boolean;
  readonly authError: string;
  readonly signInWithPassword: (email: string, password: string) => Promise<void>;
  readonly requestPasswordReset: (email: string) => Promise<void>;
  readonly confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  readonly signInWithCognito: () => Promise<void>;
  readonly signOut: () => void;
  readonly authHeaders: Record<string, string>;
  readonly sessionExpiresAt: number | null;
  readonly sessionWillExpireSoon: boolean;
  readonly refreshSession: () => Promise<boolean>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);
const STORAGE_KEY = 'crime-platform-session';
const PKCE_VERIFIER_KEY = 'crime-platform-pkce-verifier';
const PKCE_STATE_KEY = 'crime-platform-pkce-state';
const RETURN_URL_KEY = 'crime-platform-return-url';
const AUTH_NOTICE_KEY = 'crime-platform-auth-notice';
const BROADCAST_CHANNEL_NAME = 'crime-platform-auth';
const EXPIRY_WARNING_WINDOW_MS = 2 * 60_000;
const PREEMPTIVE_REFRESH_WINDOW_MS = 5 * 60_000;

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [authHeaders, setAuthHeaders] = useState<Record<string, string>>({});
  const [authBootstrapped, setAuthBootstrapped] = useState(false);
  const [authError, setAuthError] = useState('');
  const [sessionMode, setSessionMode] = useState<'password' | 'oauth' | null>(null);
  const [sessionTokens, setSessionTokens] = useState<CognitoTokens | null>(null);
  const [clockNowMs, setClockNowMs] = useState(Date.now());

  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const sessionModeRef = useRef<'password' | 'oauth' | null>(null);
  const sessionTokensRef = useRef<CognitoTokens | null>(null);
  const principalRef = useRef<Principal | null>(null);
  const authConfigRef = useRef<AuthConfig | null>(null);
  const broadcastRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    sessionModeRef.current = sessionMode;
  }, [sessionMode]);

  useEffect(() => {
    sessionTokensRef.current = sessionTokens;
  }, [sessionTokens]);

  useEffect(() => {
    principalRef.current = principal;
  }, [principal]);

  useEffect(() => {
    authConfigRef.current = authConfig;
  }, [authConfig]);

  const clearSessionState = useCallback((message = '') => {
    setPrincipal(null);
    setAuthHeaders({});
    setSessionMode(null);
    setSessionTokens(null);
    clearStoredSession();
    clearPkceSession();
    if (message) {
      sessionStorage.setItem(AUTH_NOTICE_KEY, message);
      setAuthError(message);
    }
  }, []);

  const applySession = useCallback((nextSession: StoredSession, options?: { persist?: boolean; broadcast?: boolean }) => {
    const persist = options?.persist ?? true;
    const broadcast = options?.broadcast ?? true;
    const headers = authorizationHeaders(nextSession.tokens ?? null);
    setPrincipal(nextSession.principal);
    setAuthHeaders(headers);
    setSessionMode(nextSession.mode);
    setSessionTokens(nextSession.tokens ?? null);
    setAuthError('');
    if (persist) {
      persistSession(nextSession);
    }
    if (broadcast) {
      broadcastRef.current?.postMessage({ type: 'session-updated', session: nextSession });
    }
  }, []);

  const signOutInternal = useCallback((message?: string, options?: { redirectHostedLogout?: boolean }) => {
    const shouldRedirectHostedLogout =
      options?.redirectHostedLogout !== false
      && sessionModeRef.current === 'oauth'
      && Boolean(authConfigRef.current?.cognitoDomain);
    const cfg = authConfigRef.current;
    clearSessionState(message);
    broadcastRef.current?.postMessage({ type: 'session-cleared', message: message ?? '' });

    if (shouldRedirectHostedLogout && cfg) {
      const logoutUrl = new URL(`${cfg.cognitoDomain}/logout`);
      logoutUrl.searchParams.set('client_id', cfg.cognitoClientId);
      logoutUrl.searchParams.set('logout_uri', cfg.logoutRedirectUri);
      window.location.assign(logoutUrl.toString());
    }
  }, [clearSessionState]);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const action = (async () => {
      const cfg = authConfigRef.current;
      const tokens = sessionTokensRef.current;
      const mode = sessionModeRef.current;
      const livePrincipal = principalRef.current;

      if (!cfg || !tokens?.refreshToken || !mode || !livePrincipal) {
        return false;
      }

      try {
        const refreshedTokens = await refreshHostedSession(cfg, tokens.refreshToken);
        const refreshedSession: StoredSession = {
          mode,
          principal: livePrincipal,
          tokens: refreshedTokens,
        };
        applySession(refreshedSession);
        return true;
      } catch {
        signOutInternal('Your session expired. Please sign in again to continue.', { redirectHostedLogout: false });
        return false;
      }
    })();

    refreshInFlightRef.current = action;
    try {
      return await action;
    } finally {
      refreshInFlightRef.current = null;
    }
  }, [applySession, signOutInternal]);

  useEffect(() => {
    configureApiAuthHandlers({
      refreshAuthHeaders: async () => {
        const refreshed = await refreshSession();
        if (!refreshed || !sessionTokensRef.current) {
          return null;
        }
        return authorizationHeaders(sessionTokensRef.current);
      },
    });

    return () => {
      configureApiAuthHandlers({ refreshAuthHeaders: null });
    };
  }, [refreshSession]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (process.env.NODE_ENV === 'test') {
        if (!cancelled) {
          setAuthBootstrapped(true);
        }
        return;
      }

      try {
        const nextAuthConfig = await getAuthConfig();
        if (cancelled) {
          return;
        }
        setAuthConfig(nextAuthConfig);

        if (hasOauthCallback(new URL(window.location.href))) {
          await restoreOauthCallback(nextAuthConfig, (session) => applySession(session, { persist: true, broadcast: true }));
          if (!cancelled) {
            setAuthBootstrapped(true);
          }
          return;
        }

        const stored = loadStoredSession();
        if (!stored) {
          setAuthBootstrapped(true);
          return;
        }

        if ((stored as any).mode === 'debug') {
          clearStoredSession();
          setAuthBootstrapped(true);
          return;
        }

        if (!stored.tokens) {
          clearStoredSession();
          setAuthBootstrapped(true);
          return;
        }

        let tokens = stored.tokens;
        if (tokenExpired(tokens) && tokens.refreshToken) {
          tokens = await refreshHostedSession(nextAuthConfig, tokens.refreshToken);
        }

        if (tokenExpired(tokens)) {
          clearStoredSession();
          setAuthBootstrapped(true);
          return;
        }

        const oauthHeaders = authorizationHeaders(tokens);
        const livePrincipal = await getCurrentPrincipal(oauthHeaders);
        applySession(
          {
            mode: stored.mode,
            principal: livePrincipal,
            tokens,
          },
          { persist: true, broadcast: false }
        );
      } catch (error) {
        clearStoredSession();
        clearPkceSession();
        if (!cancelled) {
          const notice = sessionStorage.getItem(AUTH_NOTICE_KEY) ?? '';
          setAuthError(notice || (error instanceof Error ? error.message : 'Failed to initialize authentication.'));
          sessionStorage.removeItem(AUTH_NOTICE_KEY);
        }
      } finally {
        if (!cancelled) {
          setAuthBootstrapped(true);
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [applySession]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastRef.current = channel;

    channel.onmessage = (event: MessageEvent<{ type?: string; session?: StoredSession; message?: string }>) => {
      const payload = event.data;
      if (payload?.type === 'session-updated' && payload.session) {
        applySession(payload.session, { persist: true, broadcast: false });
      }
      if (payload?.type === 'session-cleared') {
        clearSessionState(payload.message || 'You were signed out in another tab.');
      }
    };

    return () => {
      channel.close();
      if (broadcastRef.current === channel) {
        broadcastRef.current = null;
      }
    };
  }, [applySession, clearSessionState]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) {
        return;
      }

      if (!event.newValue) {
        clearSessionState('You were signed out in another tab.');
        return;
      }

      try {
        const session = JSON.parse(event.newValue) as StoredSession;
        if (session?.tokens) {
          applySession(session, { persist: false, broadcast: false });
        }
      } catch {
        // Ignore malformed payloads from older app versions.
      }
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [applySession, clearSessionState]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setClockNowMs(Date.now());
    }, 30_000);
    return () => {
      window.clearInterval(timerId);
    };
  }, []);

  useEffect(() => {
    if (!principal || !sessionTokens) {
      return;
    }

    const msUntilRefresh = sessionTokens.expiresAt - Date.now() - PREEMPTIVE_REFRESH_WINDOW_MS;
    if (msUntilRefresh <= 0) {
      void refreshSession();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void refreshSession();
    }, msUntilRefresh);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [principal, refreshSession, sessionTokens]);

  const value = useMemo<SessionContextValue>(
    () => ({
      principal,
      authConfig,
      authBootstrapped,
      authError,
      authHeaders,
      sessionExpiresAt: sessionTokens?.expiresAt ?? null,
      sessionWillExpireSoon: Boolean(sessionTokens && sessionTokens.expiresAt - clockNowMs <= EXPIRY_WARNING_WINDOW_MS),
      async refreshSession() {
        return refreshSession();
      },
      async signInWithPassword(email: string, password: string) {
        if (!authConfig) {
          throw new Error('Authentication configuration is not loaded.');
        }
        const tokens = await authenticateWithPassword(authConfig, email, password);
        const bearerHeaders = authorizationHeaders(tokens);
        const livePrincipal = await getCurrentPrincipal(bearerHeaders);
        applySession({
          mode: 'password',
          principal: livePrincipal,
          tokens,
        });
      },
      async requestPasswordReset(email: string) {
        if (!authConfig) {
          throw new Error('Authentication configuration is not loaded.');
        }
        await requestPasswordResetWithCognito(authConfig, email);
      },
      async confirmPasswordReset(email: string, code: string, newPassword: string) {
        if (!authConfig) {
          throw new Error('Authentication configuration is not loaded.');
        }
        await confirmPasswordResetWithCognito(authConfig, email, code, newPassword);
      },
      async signInWithCognito() {
        if (!authConfig?.cognitoDomain) {
          throw new Error('Cognito hosted sign-in is not configured.');
        }

        const loginRequest = await createHostedLoginRequest(authConfig);
        sessionStorage.setItem(PKCE_VERIFIER_KEY, loginRequest.verifier);
        sessionStorage.setItem(PKCE_STATE_KEY, loginRequest.state);
        sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search + window.location.hash);
        window.location.assign(loginRequest.authorizeUrl);
      },
      signOut() {
        signOutInternal(undefined, { redirectHostedLogout: true });
      },
    }),
    [
      principal,
      authConfig,
      authBootstrapped,
      authError,
      authHeaders,
      sessionTokens,
      clockNowMs,
      refreshSession,
      applySession,
      signOutInternal,
    ]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used inside SessionProvider.');
  }

  return context;
}

async function restoreOauthCallback(
  authConfig: AuthConfig,
  setSessionState: (session: StoredSession) => void
) {
  const callbackUrl = new URL(window.location.href);
  const code = callbackUrl.searchParams.get('code');
  const state = callbackUrl.searchParams.get('state');
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);

  if (!code || !state || !verifier || !expectedState || state !== expectedState) {
    throw new Error('Hosted sign-in callback validation failed.');
  }

  const tokens = await exchangeAuthorizationCode(authConfig, code, verifier);
  const headers = authorizationHeaders(tokens);
  const principal = await getCurrentPrincipal(headers);
  setSessionState({
    mode: 'oauth',
    principal,
    tokens,
  });
  clearPkceSession();

  const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
  sessionStorage.removeItem(RETURN_URL_KEY);
  if (returnUrl) {
    window.history.replaceState({}, document.title, returnUrl);
  } else {
    window.history.replaceState({}, document.title, callbackUrl.pathname);
  }
}

function authorizationHeaders(tokens: CognitoTokens | null): Record<string, string> {
  if (!tokens) {
    return {};
  }

  return {
    Authorization: `${tokens.tokenType || 'Bearer'} ${tokens.idToken}`,
  };
}

function persistSession(session: StoredSession) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function loadStoredSession(): StoredSession | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

function clearStoredSession() {
  localStorage.removeItem(STORAGE_KEY);
}

function clearPkceSession() {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
}
